import React, {
    forwardRef,
    useState,
    useEffect,
    useLayoutEffect,
    useMemo,
    useCallback,
    useRef,
    useImperativeHandle,
} from "react";
import { Form, Select, Button, Typography, Tooltip } from "antd";
import {
    AimOutlined,
    BankOutlined,
    ThunderboltOutlined,
    TeamOutlined,
    GlobalOutlined,
    FileTextOutlined,
    SaveOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import frLocale from "i18n-iso-countries/langs/fr.json";
import enLocale from "i18n-iso-countries/langs/en.json";
import { useTranslation } from "react-i18next";

import { CORE_ROLE_CODES, INDUSTRY_CODES, USE_CASE_CODES, DOCUMENT_TYPE_CODES } from "./constants";
import "./AnalyseMetadataPanel.css";
import {
    getJurisdictionLabelToCode,
    getJurisdictionOptions,
    isCanadianSubdivisionCode,
} from "./jurisdictionOptions";
import {
    getMissingMetadataFields,
    normalizeMetadataSelect,
    uniqueMetadataList,
} from "../../../utils/metadataNormalizer";

const countries = require("i18n-iso-countries");
countries.registerLocale(frLocale);
countries.registerLocale(enLocale);

const { Text } = Typography;

const fieldCardStyle = {
    background: "#fff",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 10,
    border: "1px solid transparent",
    transition: "all 0.24s cubic-bezier(0.4, 0, 0.2, 1)",
};

const fieldCardHover = {
    border: "1px solid #e0e6ed",
    boxShadow: "0 3px 12px rgba(11, 61, 145, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)",
    transform: "translateY(-1px)",
};

const iconWrapStyle = (color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `${color}12`,
    color,
    fontSize: 16,
    flexShrink: 0,
});

export const sortMetadataSelectOptions = (options = [], locale = "fr") => (
    [...options].sort((a, b) => String(a.label ?? "").localeCompare(
        String(b.label ?? ""),
        locale,
        { sensitivity: "base" }
    ))
);

export const buildMetadataSelectOptions = (codes = [], labelBuilder = (code) => code, locale = "fr") => (
    sortMetadataSelectOptions(
        codes.map((code) => ({
            value: code,
            label: labelBuilder(code),
        })),
        locale
    )
);

export const buildDetectedPartyRoleOptions = (
    partyDetection,
    labelBuilder = (code) => code,
    locale = "fr"
) => {
    const parties = Array.isArray(partyDetection?.contractual_parties)
        ? partyDetection.contractual_parties
        : [];
    return sortMetadataSelectOptions(
        parties.map((party) => {
            const studiedPartyName = String(party?.legal_name || party?.display_name || "");
            const displayName = String(party?.display_name || studiedPartyName);
            const detectedPartyRole = String(party?.primary_role || "");
            const partyRole = normalizeMetadataSelect(
                detectedPartyRole,
                "party_role"
            );
            const roleLabel = labelBuilder(detectedPartyRole);
            return {
                value: JSON.stringify([studiedPartyName, partyRole]),
                label: [displayName, roleLabel]
                    .filter(Boolean)
                    .join(" — "),
                displayName,
                roleLabel,
                studiedPartyName,
                partyRole,
                nameMissing: /^\s*\[[^\]]+\]\s*$/.test(displayName),
            };
        }).filter((option) => option.studiedPartyName && option.partyRole),
        locale
    );
};

const PartyOptionContent = ({ option, missingNameLabel, selected = false }) => {
    if (!option?.studiedPartyName) return option?.label;
    return (
        <span className={selected ? "studied-party-selection" : "studied-party-option"}>
            <span className="studied-party-option__identity">
                <span className="studied-party-option__name">{option.displayName}</span>
                {option.nameMissing && !selected && (
                    <span className="studied-party-option__hint">{missingNameLabel}</span>
                )}
            </span>
            <span className="studied-party-option__role">{option.roleLabel}</span>
        </span>
    );
};

const decodeDetectedPartySelection = (value) => {
    if (typeof value !== "string" || !value.startsWith("[")) return null;
    try {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed) || parsed.length !== 2) return null;
        const [studiedPartyName, partyRole] = parsed;
        if (typeof studiedPartyName !== "string" || typeof partyRole !== "string") return null;
        if (!studiedPartyName || !partyRole) return null;
        return { studiedPartyName, partyRole };
    } catch {
        return null;
    }
};

export const buildCanonicalMetadataPayload = ({
    values = {},
    locale = "fr",
    normalizeJurisdictionSelect = (value) => uniqueMetadataList(value, "jurisdiction"),
} = {}) => {
    const industryCodes = uniqueMetadataList(values.industry, "industry");
    const jurisdictionVals = normalizeJurisdictionSelect(values.jurisdiction);
    const documentType = normalizeMetadataSelect(values.document_type, "document_type");
    const detectedParty = decodeDetectedPartySelection(values.party_selection);
    const partyRole = normalizeMetadataSelect(
        detectedParty?.partyRole ?? values.party_selection ?? values.party_role,
        "party_role"
    );
    const studiedPartyName = detectedParty?.studiedPartyName
        || normalizeMetadataSelect(values.studied_party_name, "studied_party_name");

    return {
        ...values,
        context: normalizeMetadataSelect(values.context, "context"),
        document_type: DOCUMENT_TYPE_CODES.includes(documentType) ? documentType : undefined,
        party_role: partyRole,
        studied_party_name: studiedPartyName,
        industry: industryCodes,
        jurisdiction: jurisdictionVals,
        context_code: values.context,
        party_role_code: partyRole,
        industry_codes: industryCodes,
        language: locale,
    };
};

const FieldCard = ({ icon, color, label, tooltip, children, animStyle, highlighted = false }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            className={highlighted ? "metadata-field-card metadata-field-card--ai" : "metadata-field-card"}
            style={{
                ...fieldCardStyle,
                ...(hovered ? fieldCardHover : {}),
                ...animStyle,
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div className="metadata-icon-wrap" style={iconWrapStyle(color)}>{icon}</div>
                <span className="metadata-field-card__label">
                    <Text strong style={{ fontSize: 13.5, color: "#262626", letterSpacing: "-0.01em" }}>{label}</Text>
                    {tooltip && (
                        <Tooltip title={tooltip} placement="topLeft">
                            <InfoCircleOutlined className="metadata-field-card__tooltip-icon" />
                        </Tooltip>
                    )}
                </span>
            </div>
            {children}
        </div>
    );
};

const AnalyseMetadataPanel = forwardRef(({
    mode = "create",
    initialValues,
    onChangeDraft,
    onSave,
    onPovChange,
    onGuess,
    guessing = false,
    loading = false,
    disabled = false,
    showSaveButton = true,
    saveButtonLabel,
    compact = false,
    requireCompleteBeforeSave = false,
    showAiGuessButton = true,
    partyDetection = null,
    partyDetectionStatus = "not_started",
    povTransitioning = false,
    povFocusRequest = 0,
}, ref) => {
    const [form] = Form.useForm();
    const [highlightedFields, setHighlightedFields] = useState([]);
    const [isAiCtaLeaving, setIsAiCtaLeaving] = useState(false);
    const [isAiCtaCollapsing, setIsAiCtaCollapsing] = useState(false);
    const [isAiCtaEntering, setIsAiCtaEntering] = useState(false);
    const [partySelectOpen, setPartySelectOpen] = useState(false);
    const [liveValues, setLiveValues] = useState({
        context: undefined,
        document_type: undefined,
        party_selection: undefined,
        industry: [],
        jurisdiction: [],
    });
    const highlightTimeoutRef = useRef(null);
    const ctaCollapseTimeoutRef = useRef(null);
    const ctaEnterTimeoutRef = useRef(null);
    // Animation state is derived from real visibility transitions, not from "AI was used" history.
    const hasObservedCtaStateRef = useRef(false);
    const wasAiCtaVisibleRef = useRef(false);
    const didApplyGuessRef = useRef(false);
    const partySelectRef = useRef(null);

    const { t: tTax, i18n } = useTranslation("taxonomie");
    const locale = i18n.language?.startsWith("fr") ? "fr" : "en";

    const tr = useCallback(
        (key, fallback, options = {}) => tTax(key, { defaultValue: fallback ?? key, ...options }),
        [tTax]
    );

    const countryEntries = useMemo(() => {
        const namesObj = countries.getNames(locale, { select: "official" });
        return Object.entries(namesObj)
            .sort(([, a], [, b]) => a.localeCompare(b, locale, { sensitivity: "base" }));
    }, [locale]);

    const jurisdictionOptions = useMemo(
        () => getJurisdictionOptions(countryEntries, locale),
        [countryEntries, locale]
    );

    const normalizeJurisdictionSelect = useCallback((value) => {
        const labelToCode = getJurisdictionLabelToCode(countryEntries);
        return uniqueMetadataList(value, "jurisdiction")
            .map((item) => {
                const raw = String(item).trim();
                const upper = raw.toUpperCase();
                if (/^[A-Z]{2}$/.test(upper) && countries.isValid(upper)) return upper;
                if (isCanadianSubdivisionCode(upper)) return upper;
                return labelToCode.get(raw.toLowerCase()) || raw;
            })
            .filter(Boolean);
    }, [countryEntries]);

    const normalizedInitialValues = useMemo(() => {
        const src = initialValues || {};
        const documentType = normalizeMetadataSelect(src.document_type, "document_type");
        const hasDetectedParties = Array.isArray(partyDetection?.contractual_parties)
            && partyDetection.contractual_parties.length > 0;
        return {
            context: normalizeMetadataSelect(src.context, "context"),
            document_type: DOCUMENT_TYPE_CODES.includes(documentType) ? documentType : undefined,
            party_selection: src.studied_party_name && src.party_role
                ? JSON.stringify([
                    src.studied_party_name,
                    normalizeMetadataSelect(src.party_role, "party_role"),
                ])
                : (hasDetectedParties ? undefined : normalizeMetadataSelect(src.party_role, "party_role")),
            studied_party_name: normalizeMetadataSelect(src.studied_party_name, "studied_party_name"),
            industry: uniqueMetadataList(src.industry, "industry"),
            jurisdiction: normalizeJurisdictionSelect(src.jurisdiction),
        };
    }, [initialValues, normalizeJurisdictionSelect, partyDetection]);

    useEffect(() => {
        if (didApplyGuessRef.current) return;
        const touched = form.isFieldsTouched(true);
        if (!touched) {
            form.setFieldsValue(normalizedInitialValues);
            setLiveValues(normalizedInitialValues);
        }
    }, [form, normalizedInitialValues]);

    const taxonomyOptions = useCallback(
        (codes, section) =>
            buildMetadataSelectOptions(codes, (code) => tr(`taxonomy.${section}.${code}`, code), locale),
        [locale, tr]
    );

    const detectedRoleOptions = useMemo(
        () => buildDetectedPartyRoleOptions(
            partyDetection,
            (code) => tr(
                `analysis.party_role_labels.${code}`,
                tr(`taxonomy.role.${code}`, code)
            ),
            locale
        ),
        [locale, partyDetection, tr]
    );
    const roleOptions = useMemo(
        () => detectedRoleOptions.length
            ? detectedRoleOptions
            : taxonomyOptions(CORE_ROLE_CODES, "role"),
        [detectedRoleOptions, taxonomyOptions]
    );

    const canonicalizeForBackend = useCallback((values) => {
        return buildCanonicalMetadataPayload({
            values,
            locale,
            normalizeJurisdictionSelect,
        });
    }, [locale, normalizeJurisdictionSelect]);

    const requiredRule = useCallback((label) => (
        requireCompleteBeforeSave
            ? [{ required: true, message: tr("analysis.required_field", `${label} est requis`, { field: label }) }]
            : []
    ), [requireCompleteBeforeSave, tr]);

    const requiredMultiRule = useCallback((label) => (
        requireCompleteBeforeSave
            ? [{
                type: "array",
                min: 1,
                required: true,
                message: tr("analysis.required_field", `${label} est requis`, { field: label }),
            }]
            : []
    ), [requireCompleteBeforeSave, tr]);

    const handleSave = useCallback(async () => {
        const values = await form.validateFields();
        const payload = canonicalizeForBackend(values);
        if (onSave) await onSave(payload);
    }, [form, onSave, canonicalizeForBackend]);

    const handleValuesChange = useCallback(
        (changedValues, allValues) => {
            didApplyGuessRef.current = false;
            setLiveValues(allValues);
            if (getMissingMetadataFields(canonicalizeForBackend(allValues)).length > 0 && (isAiCtaLeaving || isAiCtaCollapsing)) {
                if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
                if (ctaCollapseTimeoutRef.current) clearTimeout(ctaCollapseTimeoutRef.current);
                setHighlightedFields([]);
                setIsAiCtaLeaving(false);
                setIsAiCtaCollapsing(false);
            }
            const canonicalValues = canonicalizeForBackend(allValues);
            onChangeDraft?.(canonicalValues);
            if (
                Object.prototype.hasOwnProperty.call(changedValues || {}, "party_selection")
                && onPovChange
            ) {
                Promise.resolve(onPovChange({
                    party_role: canonicalValues.party_role || null,
                    studied_party_name: canonicalValues.studied_party_name || null,
                })).catch(() => {
                    // The metadata hook owns rollback and user-facing feedback.
                });
            }
        },
        [
            isAiCtaCollapsing,
            isAiCtaLeaving,
            onChangeDraft,
            onPovChange,
            canonicalizeForBackend,
        ]
    );

    const missingFields = useMemo(
        () => getMissingMetadataFields(canonicalizeForBackend(liveValues)),
        [canonicalizeForBackend, liveValues]
    );

    const keepAllowed = useCallback((values, allowed) => {
        const allowedSet = new Set(allowed);
        return uniqueMetadataList(values).filter((value) => allowedSet.has(value));
    }, []);

    const handleGuess = useCallback(async () => {
        if (!onGuess || guessing || missingFields.length === 0) return;

        const guessed = await onGuess(jurisdictionOptions.map((option) => option.value));
        if (!guessed) return;

        const currentValues = form.getFieldsValue(true);
        const currentMetadata = canonicalizeForBackend(currentValues);
        const currentIndustry = uniqueMetadataList(currentValues?.industry, "industry");
        const currentJurisdiction = normalizeJurisdictionSelect(currentValues?.jurisdiction);

        const nextValues = {
            context: normalizeMetadataSelect(currentValues?.context, "context") || (USE_CASE_CODES.includes(guessed.context) ? guessed.context : undefined),
            document_type: normalizeMetadataSelect(currentValues?.document_type, "document_type") || (DOCUMENT_TYPE_CODES.includes(guessed.document_type) ? guessed.document_type : undefined),
            party_selection: currentValues?.party_selection || (roleOptions.some((option) => option.value === guessed.party_role) ? guessed.party_role : undefined),
            industry: currentIndustry.length ? currentIndustry : keepAllowed(guessed.industry, INDUSTRY_CODES),
            jurisdiction: currentJurisdiction.length ? currentJurisdiction : keepAllowed(guessed.jurisdiction, jurisdictionOptions.map((option) => option.value)),
        };

        const newlyFilled = [];
        if (!normalizeMetadataSelect(currentValues?.context, "context") && normalizeMetadataSelect(nextValues.context, "context")) newlyFilled.push("context");
        if (!normalizeMetadataSelect(currentValues?.document_type, "document_type") && normalizeMetadataSelect(nextValues.document_type, "document_type")) newlyFilled.push("document_type");
        if (!currentMetadata.party_role && canonicalizeForBackend(nextValues).party_role) newlyFilled.push("party_role");
        if (!currentIndustry.length && uniqueMetadataList(nextValues.industry, "industry").length) newlyFilled.push("industry");
        if (!currentJurisdiction.length && uniqueMetadataList(nextValues.jurisdiction, "jurisdiction").length) newlyFilled.push("jurisdiction");

        form.setFieldsValue(nextValues);
        didApplyGuessRef.current = true;
        setLiveValues(nextValues);
        onChangeDraft?.(canonicalizeForBackend(nextValues));

        setHighlightedFields(newlyFilled);
        setIsAiCtaLeaving(true);
        setIsAiCtaCollapsing(false);
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        if (ctaCollapseTimeoutRef.current) clearTimeout(ctaCollapseTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedFields([]);
            setIsAiCtaCollapsing(true);
        }, 820);
        ctaCollapseTimeoutRef.current = setTimeout(() => {
            setIsAiCtaLeaving(false);
            setIsAiCtaCollapsing(false);
        }, 1100);
    }, [jurisdictionOptions, form, guessing, keepAllowed, canonicalizeForBackend, missingFields.length, normalizeJurisdictionSelect, onChangeDraft, onGuess, roleOptions]);

    useImperativeHandle(ref, () => ({
        submitMetadata: handleSave,
        guessMissingMetadata: handleGuess,
        focusPartySelection: () => partySelectRef.current?.focus?.(),
        getCurrentMetadata: () => canonicalizeForBackend(form.getFieldsValue(true)),
    }), [canonicalizeForBackend, form, handleGuess, handleSave]);

    useEffect(() => {
        if (!povFocusRequest) return;
        const timer = window.setTimeout(() => partySelectRef.current?.focus?.(), 0);
        return () => window.clearTimeout(timer);
    }, [povFocusRequest]);

    useEffect(() => {
        if (povTransitioning) setPartySelectOpen(false);
    }, [povTransitioning]);

    useEffect(() => () => {
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        if (ctaCollapseTimeoutRef.current) clearTimeout(ctaCollapseTimeoutRef.current);
        if (ctaEnterTimeoutRef.current) clearTimeout(ctaEnterTimeoutRef.current);
    }, []);

    const showAiGuessCta = showAiGuessButton && !!onGuess && (missingFields.length > 0 || isAiCtaLeaving || isAiCtaCollapsing);

    useLayoutEffect(() => {
        const visible = !!onGuess && (missingFields.length > 0 || isAiCtaLeaving || isAiCtaCollapsing);
        const enteredFromHidden = visible && !wasAiCtaVisibleRef.current;
        const shouldAnimateEntry = enteredFromHidden && missingFields.length > 0 && !isAiCtaLeaving && !isAiCtaCollapsing;

        if (!hasObservedCtaStateRef.current) {
            hasObservedCtaStateRef.current = true;
            wasAiCtaVisibleRef.current = visible;
            return;
        }

        if (shouldAnimateEntry) {
            setIsAiCtaEntering(true);
            if (ctaEnterTimeoutRef.current) clearTimeout(ctaEnterTimeoutRef.current);
            ctaEnterTimeoutRef.current = setTimeout(() => setIsAiCtaEntering(false), 280);
        } else if (!visible) {
            setIsAiCtaEntering(false);
        }

        wasAiCtaVisibleRef.current = visible;
    }, [isAiCtaCollapsing, isAiCtaLeaving, missingFields.length, onGuess]);

    return (
        <div
            className={compact ? "metadata-panel--compact" : undefined}
            style={{ padding: compact ? "8px 14px 10px" : "12px 16px 16px" }}
        >
            {showAiGuessCta && (
                <div
                    className={[
                        "metadata-ai-guess-row",
                        "metadata-ai-guess-row--magic",
                        isAiCtaEntering ? "metadata-ai-guess-row--entering" : "",
                        isAiCtaLeaving && missingFields.length === 0 ? "metadata-ai-guess-row--leaving" : "",
                        isAiCtaCollapsing && missingFields.length === 0 ? "metadata-ai-guess-row--collapsing" : "",
                    ].filter(Boolean).join(" ")}
                >
                    {(!compact || (isAiCtaLeaving && missingFields.length === 0)) && (
                        <div className="metadata-ai-guess-row__left">
                            <span className="metadata-ai-guess-row__icon"><ThunderboltOutlined /></span>
                            <span className="metadata-ai-guess-row__text">
                                {isAiCtaLeaving && missingFields.length === 0
                                    ? tr("analysis.ai_guess_completed", "Champs completes")
                                    : tr("analysis.ai_guess_compact", "Completer les champs manquants via IA")}
                            </span>
                        </div>
                    )}
                    {!(isAiCtaLeaving && missingFields.length === 0) && (
                        <Button
                            type="primary"
                            loading={guessing}
                            onClick={handleGuess}
                            icon={<ThunderboltOutlined />}
                            className="metadata-ai-guess-row__button verdictia-ai-magic-btn"
                        >
                            {guessing
                                ? tr("analysis.ai_guess_loading", "Analyse en cours")
                                : tr("analysis.ai_guess_button", "Generer via IA")}
                        </Button>
                    )}
                </div>
            )}

            <Form
                className="metadata-form-stack"
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
                disabled={disabled || loading}
            >
                <FieldCard highlighted={highlightedFields.includes("context")} animStyle={{ animation: "fieldCardIn 0.4s cubic-bezier(0.16,1,0.3,1) 0s both" }} icon={<AimOutlined />} color="#0B3D91" label={tr("analysis.context")} tooltip={tr("analysis.context_tooltip")}>
                    <Form.Item name="context" style={{ marginBottom: 0 }} rules={requiredRule(tr("analysis.context"))}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder={tr("analysis.context_placeholder")}
                            allowClear
                            options={taxonomyOptions(USE_CASE_CODES, "use_case")}
                        />
                    </Form.Item>
                </FieldCard>

                <FieldCard highlighted={highlightedFields.includes("document_type")} animStyle={{ animation: "fieldCardIn 0.4s cubic-bezier(0.16,1,0.3,1) 0.05s both" }} icon={<FileTextOutlined />} color="#0851cc" label={tr("analysis.document_type")} tooltip={tr("analysis.document_type_tooltip")}>
                    <Form.Item name="document_type" style={{ marginBottom: 0 }} rules={requiredRule(tr("analysis.document_type"))}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder={tr("analysis.document_type_placeholder")}
                            allowClear
                            options={buildMetadataSelectOptions(
                                DOCUMENT_TYPE_CODES,
                                (code) => tr(`documentType.${code}`, code),
                                locale
                            )}
                        />
                    </Form.Item>
                </FieldCard>

                <FieldCard highlighted={highlightedFields.includes("industry")} animStyle={{ animation: "fieldCardIn 0.4s cubic-bezier(0.16,1,0.3,1) 0.07s both" }} icon={<BankOutlined />} color="#50B5FF" label={tr("analysis.industry")} tooltip={tr("analysis.industry_tooltip")}>
                    <Form.Item name="industry" style={{ marginBottom: 0 }} className="metadata-field-card--multi" rules={requiredMultiRule(tr("analysis.industry"))}>
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="label"
                            placeholder={tr("analysis.industry_placeholder")}
                            allowClear
                            maxTagCount={2}
                            options={taxonomyOptions(INDUSTRY_CODES, "industry")}
                        />
                    </Form.Item>
                </FieldCard>

                <FieldCard highlighted={highlightedFields.includes("party_role")} animStyle={{ animation: "fieldCardIn 0.4s cubic-bezier(0.16,1,0.3,1) 0.14s both" }} icon={<TeamOutlined />} color="#0B3D91" label={tr("analysis.studied_party")} tooltip={tr("analysis.studied_party_tooltip")}>
                    <Form.Item name="party_selection" style={{ marginBottom: 0 }} rules={requiredRule(tr("analysis.studied_party"))}>
                        <Select
                            ref={partySelectRef}
                            showSearch
                            optionFilterProp="label"
                            placeholder={tr("analysis.studied_party_placeholder")}
                            allowClear
                            open={partySelectOpen && !povTransitioning}
                            onOpenChange={(nextOpen) => {
                                setPartySelectOpen(nextOpen && !povTransitioning);
                            }}
                            onSelect={() => setPartySelectOpen(false)}
                            onClear={() => setPartySelectOpen(false)}
                            disabled={povTransitioning}
                            options={roleOptions}
                            optionRender={(option) => (
                                <PartyOptionContent
                                    option={option.data}
                                    missingNameLabel={tr("analysis.party_name_missing")}
                                />
                            )}
                            labelRender={(selection) => {
                                const selectedOption = roleOptions.find(
                                    (option) => option.value === selection.value
                                );
                                return selectedOption?.studiedPartyName ? (
                                    <PartyOptionContent
                                        option={selectedOption}
                                        missingNameLabel={tr("analysis.party_name_missing")}
                                        selected
                                    />
                                ) : selection.label;
                            }}
                        />
                    </Form.Item>
                    {partyDetectionStatus === "processing" && (
                        <Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                            {tr("analysis.role_detecting", "Détection des parties en cours…")}
                        </Text>
                    )}
                    {partyDetectionStatus === "succeeded" && detectedRoleOptions.length > 0 && (
                        <Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                            {tr("analysis.role_detected", "Choix proposés à partir du contrat")}
                        </Text>
                    )}
                </FieldCard>

                <FieldCard highlighted={highlightedFields.includes("jurisdiction")} animStyle={{ animation: "fieldCardIn 0.4s cubic-bezier(0.16,1,0.3,1) 0.21s both" }} icon={<GlobalOutlined />} color="#0851cc" label={tr("analysis.jurisdiction")} tooltip={tr("analysis.jurisdiction_tooltip")}>
                    <Form.Item name="jurisdiction" style={{ marginBottom: 0 }} className="metadata-field-card--multi" rules={requiredMultiRule(tr("analysis.jurisdiction"))}>
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="label"
                            placeholder={tr("analysis.jurisdiction_placeholder")}
                            allowClear
                            maxTagCount={3}
                            options={jurisdictionOptions}
                        />
                    </Form.Item>
                    {povTransitioning && (
                        <div
                            className="metadata-pov-transition"
                            role="status"
                            aria-live="polite"
                        >
                            <LoadingOutlined spin aria-hidden="true" />
                            <span>
                                {tr(
                                    "analysis.pov_transition_loading",
                                    "Mise à jour du point de vue et chargement des résultats…"
                                )}
                            </span>
                        </div>
                    )}
                </FieldCard>

                {showSaveButton && (
                    <div className="metadata-save-row" style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                        <Button
                            type="primary"
                            loading={loading}
                            disabled={disabled || loading || povTransitioning}
                            onClick={handleSave}
                            icon={<SaveOutlined />}
                            style={{
                                borderRadius: 8,
                                height: 36,
                                fontWeight: 600,
                                boxShadow: "0 2px 8px rgba(22,119,255,0.25)",
                            }}
                        >
                            {saveButtonLabel || tr("common.save")}
                        </Button>
                    </div>
                )}
            </Form>
        </div>
    );
});

AnalyseMetadataPanel.displayName = "AnalyseMetadataPanel";

export default AnalyseMetadataPanel;
